import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { trapFocus } from '@theme/focus';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @type {{ type: 'row', key: string, removedKeys: string[] } | { type: 'empty', removedKeys: string[] } | null} */
  #pendingRemovalFocusTarget = null;

  /** @type {Array<ReturnType<typeof setTimeout>>} */
  #removalFocusVerificationTimers = [];

  /** @type {HTMLElement | null} */
  #emptyRemovalTrapRefreshTarget = null;

  /** @type {boolean} */
  #pendingRemovalHadUserInput = false;

  /**
   * TCS A11Y-LA-07 (R-028d): the control in the continued row the visitor
   * focused (or was on when they gave input) while the removal was pending, plus
   * what identifies its equivalent after a morph replaces the node.
   * @type {{ node: HTMLElement, key: string, signature: { tag: string, ref: string | null, name: string | null, className: string, index: number } } | null}
   */
  #deliberateFocus = null;

  #isRestoringRemovalFocus = false;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.#showReloadNotice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * TCS V02-01 / A11Y-LA-06 (R-028; R-028a): while a line
   * removal is in flight, every later row's data-cart-line is stale until the
   * section morph, and R-028 puts keyboard focus on that row. Ignore quantity
   * changes then — at dispatch (the 300 ms debounce can outlast the request) and
   * again when handled. Quantity-only requests are not blocked: they do not
   * renumber lines, so behavior there stays as upstream.
   * @param {Event} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (this.#ignoreQuantityChangeDuringRemoval(event)) return;
    this.#stampQuantityPress(event);
    this.#debouncedOnChange(event);
  };

  /** @type {number} */
  #removalsInFlight = 0;

  /** @type {Set<Element>} */
  #quantityControlsIgnoredDuringRemoval = new Set();

  /** @type {number} */
  #cartRecoveryRequestSequence = 0;

  /**
   * Rows whose optimistic removal animation is still pending for the in-flight
   * request, so a failed-removal recovery can abort it. See
   * #abortPendingRemovalAnimation.
   * @type {HTMLElement[]}
   */
  #pendingRemovalRows = [];

  /**
   * The optimistic empty-cart swap waiting for its view-transition callback, so a
   * failed-removal recovery that settles first can cancel it (R-037). See
   * #abortPendingRemovalAnimation.
   * @type {object | null}
   */
  #pendingEmptyCartSwap = null;

  /**
   * Rows whose pending `onAnimationEnd` removal must become a no-op because the
   * recovery already restored them.
   * @type {WeakSet<Element>}
   */
  #abortedRemovalRows = new WeakSet();

  /**
   * Captured before optimistic removal mutates the row. Used only if that
   * removal fails and the server-rendered row is restored.
   * @type {{
   *   key: string,
   *   signature: { tag: string, ref: string | null, name: string | null, className: string, index: number },
   *   automaticTarget: null | { type: 'row', key: string, signature: { tag: string, ref: string | null, name: string | null, className: string, index: number } } | { type: 'empty' }
   * } | null}
   */
  #failedRemovalFocusSnapshot = null;

  /**
   * TCS A11Y-LA-07 (R-028b): monotonic line generation, bumped whenever a
   * response that renumbers lines (a removal) is applied. A press dispatched
   * before such a response but handled after it — the 300 ms debounce straddling
   * a fast removal — carries a `cartLine` that no longer names the row the
   * shopper pressed, and neither R-028a checkpoint sees it (`#removalsInFlight`
   * is 0 at both).
   *
   * COUPLING NOTE (Horizon 4.1.3) — the vendored contracts this guard depends
   * on, with their `horizon-baseline.json` hashes. None of these files is owned,
   * so `check:horizon` fails on any drift; re-verify each one here when its hash
   * moves (R-026 pattern).
   *
   * - `assets/events.js` (`c64ccba5392db7ea`): `QuantitySelectorUpdateEvent`
   *   carries `detail.cartLine`.
   * - `assets/component-quantity-selector.js` (`37e2bd5e160d762f`): builds that
   *   number from `input[ref="quantityInput"]`'s `data-cart-line` and dispatches
   *   on the selector element, so `event.target` is that control; its
   *   `updateButtonStates()` is public (R-028a's settle path calls it too).
   * - `snippets/quantity-selector.liquid` (`988281bf3e59a033`): renders
   *   `data-cart-line` 1-based, re-rendered on every section morph.
   * - `snippets/cart-products.liquid` (`b39a99d5e9f3d02f`): rows carry
   *   `data-key`, which `cartItemRows[line - 1].dataset.key` reads, and
   *   `id="CartItem-{{ item.key }}"`.
   * - `assets/morph.js` (`2b73e34463a67247`): `getNodeKey` defaults to `node.id`
   *   and `MORPH_OPTIONS` sets no override, so rows are matched by that
   *   `CartItem-<key>` id and the selector element inside a surviving row is
   *   updated in place rather than replaced. That is what lets the re-resolution
   *   read the *new* `data-cart-line`, and R-028a's settle path read the new
   *   `quantityInput.defaultValue`, from the same element the press came from.
   * - `assets/section-renderer.js` (`2fd16423bf76105e`): **ordering dependency.**
   *   `morphSection()` is declared `async` but its body contains no `await`, so
   *   `morph()` has already mutated the DOM when the call returns. The
   *   generation bump in `updateQuantity`, written on the line after an
   *   un-awaited `morphSection(...)`, is ordered after the renumbering only
   *   because of that.
   * - `#debouncedOnChange` coalesces to the last event, so a per-event stamp
   *   coalesces with it.
   *
   * Measured limits (2026-09-21, local regression, Chromium 153 / WebKit 26.6;
   * `docs/accessibility/evidence/2026-09-18/lane-a/LA-07.md`). Two drift shapes
   * degrade to discarding the press: `data-cart-line` frozen at its pre-removal
   * value (pinned by `straddling-press-stale-line-discarded`), and
   * `data-cart-line` moved off the quantity input. A third does **not**: make
   * `morphSection` genuinely deferred — await a timer before `morph()`, the
   * shape `#handleCartUpdate` already takes through `startViewTransition` — and
   * the generation bumps before the DOM is renumbered, the key check reads
   * pre-morph markup and matches, and the guard sends the stale line
   * (`sent 2:0 3:2`, measured). An upgrade that defers the morph must move the
   * bump to after the morph has actually applied; re-baselining this file
   * instead reinstates the LA-07 defect with the guard fully engaged.
   * @type {number}
   */
  #lineGeneration = 0;

  /**
   * The line generation, and the cart line key, a quantity press was made
   * against. Weakly keyed by the event so a press coalesced away by the debounce
   * takes its stamp with it.
   * @type {WeakMap<Event, { generation: number, key: string }>}
   */
  #quantityPressStamps = new WeakMap();

  /**
   * The cart rows as currently rendered.
   *
   * COUPLING NOTE (Horizon 4.1.3), `assets/component.js` (`f5529899472288cc`):
   * refs are refreshed from a `MutationObserver` whose descendant test is false
   * for a node that has already been removed, so a removal that mutates nothing
   * else inside the component leaves `refs.cartItemRows` holding a **detached**
   * row, and `cartItemRows[line - 1]` then names a different line from the one
   * the server would. Measured 2026-09-21 by
   * `straddling-press-stale-line-discarded`: DOM `A,C,D`, refs `A,B,C,D` with
   * `B` disconnected. A cart line addresses a rendered row, so resolve keys
   * against connected rows only.
   * @returns {Element[]}
   */
  #renderedCartItemRows() {
    return (this.refs.cartItemRows ?? []).filter((row) => row.isConnected);
  }

  /**
   * Stamps a quantity press with the line generation and the key of the row it
   * names, at dispatch time.
   * @param {Event} event
   */
  #stampQuantityPress(event) {
    const line = event instanceof QuantitySelectorUpdateEvent ? event.detail?.cartLine : undefined;
    const key =
      typeof line === 'number' ? (this.#renderedCartItemRows()[line - 1]?.dataset.key ?? '') : '';

    this.#quantityPressStamps.set(event, { generation: this.#lineGeneration, key });
  }

  /**
   * TCS A11Y-LA-07 (R-028b): resolves the line a debounced quantity press must
   * use. A press made in the current generation is upstream behavior, unchanged.
   * A stale press is re-resolved from the originating control's current
   * `data-cart-line` and accepted only when that line still resolves to the same
   * line-item key; otherwise it is discarded and the control is restored through
   * R-028a's settle path (that path cannot restore it later here, because the
   * removal has already settled).
   * @param {Event} event
   * @param {number} dispatchedLine - The line captured when the press was dispatched.
   * @returns {number | null} The line to send, or null when the press is discarded.
   */
  #resolveQuantityPressLine(event, dispatchedLine) {
    const stamp = this.#quantityPressStamps.get(event);

    if (!stamp || stamp.generation === this.#lineGeneration) return dispatchedLine;

    const control = event.target instanceof Element ? event.target : null;
    const currentLine = control ? this.#cartLineOfControl(control) : null;
    const currentKey = currentLine
      ? (this.#renderedCartItemRows()[currentLine - 1]?.dataset.key ?? '')
      : '';

    if (stamp.key && currentLine && currentKey === stamp.key) return currentLine;

    if (control) this.#restoreQuantityControl(control);
    return null;
  }

  /**
   * The 1-based cart line a quantity selector currently renders.
   * @param {Element} control
   * @returns {number | null}
   */
  #cartLineOfControl(control) {
    const quantityInput = control.querySelector('input[ref="quantityInput"]');
    if (!(quantityInput instanceof HTMLInputElement)) return null;

    const line = Number(quantityInput.dataset.cartLine);
    return Number.isInteger(line) && line > 0 ? line : null;
  }

  /**
   * @param {Event} event
   * @returns {boolean} Whether the change was ignored.
   */
  #ignoreQuantityChangeDuringRemoval(event) {
    if (this.#removalsInFlight === 0) return false;
    if (!(event.target instanceof Element) || !this.contains(event.target)) return false;

    this.#quantityControlsIgnoredDuringRemoval.add(event.target);
    this.#announceCartBusy();
    return true;
  }

  /**
   * After the last in-flight removal settles: a successful morph has already
   * restored ignored controls to the cart quantity; a failed request has no morph,
   * so show their last rendered value again.
   * @param {boolean} morphed
   */
  #settleQuantityControlsIgnoredDuringRemoval(morphed) {
    const controls = [...this.#quantityControlsIgnoredDuringRemoval];
    this.#quantityControlsIgnoredDuringRemoval.clear();
    if (morphed) return;

    for (const control of controls) this.#restoreQuantityControl(control);
  }

  /**
   * Shows a control its last server-rendered value again, without moving focus.
   * The single restore path for an ignored (R-028a) or discarded (R-028b) press.
   * @param {Element} control
   */
  #restoreQuantityControl(control) {
    if (!control.isConnected) return;

    const quantityInput = control.querySelector('input[ref="quantityInput"]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;
    /** @type {any} */ (control).updateButtonStates?.();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#ignoreQuantityChangeDuringRemoval(event)) return;

    const { quantity, cartLine: dispatchedLine } = event.detail;

    // Cart items require a line number
    if (!dispatchedLine) return;

    // TCS A11Y-LA-07 (R-028b): a press that straddled a removal response names a
    // line that has been renumbered. Re-resolve it by key, or discard it.
    const line = this.#resolveQuantityPressLine(event, dispatchedLine);
    if (line === null) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - The activating event, when called from the Remove button.
   */
  onLineItemRemove(line, event) {
    // TCS V02-01 (R-028; R-028a): removal focus continues
    // onto the next Remove while the request is in flight, but its line index is
    // stale until the section morph. Match `.cart-items-disabled { pointer-events:
    // none }` for keyboard/AT activation. The class is shared by every request and
    // any settling request clears it, so also check the removal counter.
    if (this.classList.contains('cart-items-disabled') || this.#removalsInFlight > 0) {
      this.#announceCartBusy();
      return;
    }

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
      return;
    }

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    this.#prepareRemovalFocusTarget(cartItemRowToRemove, rowsToRemove);
    this.#captureFailedRemovalFocusSnapshot(cartItemRowToRemove, event?.target);

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);
      const focusTarget = this.#pendingRemovalFocusTarget;
      const swap = {};
      this.#pendingEmptyCartSwap = swap;

      const emptyCartTransition = startViewTransition(() => {
        // TCS #421 (R-028c, R-037): the update callback is async. A failure that
        // settles first has already restored the line; emptying the cart over it
        // would show a false empty cart.
        if (this.#pendingEmptyCartSwap !== swap) return;
        this.#pendingEmptyCartSwap = null;
        document.getElementById('cart-drawer-heading')?.remove();
        // TCS #394 follow-up (R-028e): keep the status region connected. A region
        // removed here and re-created by the recovery morph is new when the failure
        // copy is written, and VoiceOver does not speak it. Its own tag and stable id
        // let the morph keep the same node. It goes AFTER the empty content: on a
        // successful removal the server's empty state has no region, so the empty
        // content stays first and is morphed in place, and focus on it is not lost.
        const status = this.#cartStatusRegion();
        if (status?.parentElement === this) {
          if (this.#cartStatusTimer) clearTimeout(this.#cartStatusTimer);
          this.#cartStatusTimer = null;
          status.textContent = '';
          for (const child of [...this.childNodes]) if (child !== status) child.remove();
          status.before(clone);
        } else {
          this.replaceChildren(clone);
        }
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
      this.#queueRemovalFocusRestore();
      emptyCartTransition.then(() => {
        if (this.#pendingRemovalFocusTarget === focusTarget) {
          this.#completeRemovalFocusRestore();
        }
      });

      return;
    }

    // Track the animating rows so a failed-removal recovery can abort them: the
    // 250 ms animation outlives a fast response, and `onAnimationEnd` closes over
    // this exact node, so without the abort it deletes the row the recovery just
    // restored. Only reachable under no-preference — reduced motion removes the
    // row synchronously below, before any morph can restore it.
    this.#pendingRemovalRows = [...rowsToRemove];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => {
        if (this.#abortedRemovalRows.has(row)) {
          this.#abortedRemovalRows.delete(row);
          this.#clearRemovalAnimationState(row);
          return;
        }
        row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    this.#queueRemovalFocusRestore();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    // TCS A11Y-LA-06 (R-028a): track removals, see #onQuantitySelectorUpdate.
    const isRemoval = config.action === 'clear';
    if (isRemoval) this.#removalsInFlight += 1;
    this.#disableCartItems();
    let morphed = false;

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    deferredUpdatePromise.promise.catch(() => {});
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        return {
          ok: response.ok !== false,
          status: response.status,
          responseText: await response.text(),
        };
      })
      .then(async ({ ok, status, responseText }) => {
        const parsedResponseText = this.#parseCartChangeResponse(responseText);

        resetShimmer(this);

        if (!ok && !parsedResponseText.errors) {
          throw new Error(`Cart update failed with status ${status}`);
        }

        if (parsedResponseText.errors) {
          if (isRemoval) {
            // TCS R-028e: a failed removal never shows Shopify's `.errors` text.
            morphed = await this.#recoverFailedRemoval(lineId, { parsedError: true });
          } else {
            this.#handleCartError(line, parsedResponseText);
          }
          this.#clearRemovalFocusRestore();
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const sectionHTML = this.#sectionHTMLFromCartResponse(parsedResponseText);
        const newSectionHTML = new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        morphed = true;
        // TCS A11Y-LA-07 (R-028b): this response renumbered every later line.
        if (isRemoval) this.#lineGeneration += 1;

        this.#updateCartQuantitySelectorButtonStates();
        if (config.action === 'clear') this.#completeRemovalFocusRestore();
      })
      .catch(async (error) => {
        console.error(error);
        if (isRemoval && lineId) {
          morphed = await this.#recoverFailedRemoval(lineId);
        }
        this.#clearRemovalFocusRestore();
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            // TCS R-028e: removals carry the localized copy, never the browser's
            // network-error string; other mutations keep Horizon's value.
            error: isRemoval ? this.#cartFailureMessage() : error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        if (isRemoval) {
          this.#removalsInFlight = Math.max(0, this.#removalsInFlight - 1);
          if (this.#removalsInFlight === 0) this.#settleQuantityControlsIgnoredDuringRemoval(morphed);
          this.#clearFailedRemovalFocusSnapshot();
          if (this.#removalsInFlight === 0) this.#pendingRemovalRows = [];
        }
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * @param {string} responseText
   * @returns {any}
   */
  #parseCartChangeResponse(responseText) {
    try {
      return JSON.parse(responseText);
    } catch {
      throw new Error('Malformed cart update response');
    }
  }

  /**
   * @param {any} parsedResponseText
   * @returns {string}
   */
  #sectionHTMLFromCartResponse(parsedResponseText) {
    return this.#validateSectionHTML(parsedResponseText?.sections?.[this.sectionId], 'Cart update response');
  }

  /**
   * @param {unknown} sectionHTML
   * @param {string} source
   * @returns {string}
   */
  #validateSectionHTML(sectionHTML, source) {
    if (typeof sectionHTML !== 'string' || sectionHTML.trim() === '') {
      throw new Error(`${source} missing section HTML`);
    }

    const sectionId = `shopify-section-${this.sectionId}`;
    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const section =
      typeof parsed.getElementById === 'function'
        ? parsed.getElementById(sectionId)
        : sectionHTML.includes(`id="${sectionId}"`) || sectionHTML.includes(`id='${sectionId}'`);
    if (!section) throw new Error(`${source} malformed section HTML`);

    return sectionHTML;
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    // TCS R-028e: a failed removal may have made this row's alert non-live.
    cartItemError.closest('.cart-item__error')?.setAttribute('role', 'alert');
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * TCS R-028c / R-028e / R-028f: the server-rendered polite status region for this
   * cart. The drawer renders it inside this component (snippets/tcs-cart-status.liquid
   * via tcs-cart-drawer-footer), so inside the modal dialog. The `/cart` page has
   * none (R-028f): callers then show no text and announce nothing.
   * @returns {HTMLElement | null}
   */
  #cartStatusRegion() {
    const status = this.#removalFocusRoot().querySelector('[data-tcs-cart-status]');
    return status instanceof HTMLElement ? status : null;
  }

  /** @type {ReturnType<typeof setTimeout> | null} */
  #cartStatusTimer = null;

  /**
   * Clears the region now and writes the message on a later task, so a repeat of
   * the same message is a real change that assistive technology announces again
   * (clearing and rewriting in one task coalesces into no change). Presses inside
   * the delay collapse into one announcement.
   * @param {string} message
   * @param {{ delay?: number, when?: () => boolean }} [options] - `when` is checked
   *   at write time; a busy message must not land after the cart has settled.
   */
  #announceCartStatus(message, { delay = 100, when } = {}) {
    if (!message || !this.#cartStatusRegion()) return;

    if (this.#cartStatusTimer) clearTimeout(this.#cartStatusTimer);
    const region = this.#cartStatusRegion();
    if (region) region.textContent = '';

    this.#cartStatusTimer = setTimeout(() => {
      this.#cartStatusTimer = null;
      if (when && !when()) return;
      const status = this.#cartStatusRegion();
      if (status) status.textContent = message;
    }, delay);
  }

  /** @returns {boolean} */
  #cartBusy() {
    return this.classList.contains('cart-items-disabled') || this.#removalsInFlight > 0;
  }

  #announceCartBusy() {
    this.#announceCartStatus(this.#cartBusyMessage(), { when: () => this.#cartBusy() });
  }

  /**
   * @returns {string}
   */
  #cartBusyMessage() {
    return this.#cartStatusRegion()?.dataset.tcsCartBusyMessage?.trim() || '';
  }

  /**
   * Localized failed-removal copy. Empty when the region is absent: no English
   * literal may reach the shopper (R-028e).
   * @returns {string}
   */
  #cartFailureMessage() {
    return this.#cartStatusRegion()?.dataset.tcsCartErrorMessage?.trim() || '';
  }

  static #reloadNoticeKey = 'tcs-cart-removal-reload-notice';

  /**
   * TCS R-028c / R-028e: the reload fallback is announced after the reload.
   * @param {string} lineKey
   */
  #rememberReloadNotice(lineKey) {
    try {
      sessionStorage.setItem(
        CartItemsComponent.#reloadNoticeKey,
        JSON.stringify({ sectionId: this.sectionId, lineKey })
      );
    } catch {
      // Storage unavailable: the reload still shows the true cart, silently.
    }
  }

  /**
   * After a reload fallback, tell the shopper the removal failed — but only when
   * the line is still in the cart. If it is gone, the removal did succeed and a
   * failure notice would be false.
   */
  #showReloadNotice() {
    let notice = null;
    try {
      const stored = sessionStorage.getItem(CartItemsComponent.#reloadNoticeKey);
      if (!stored) return;
      notice = JSON.parse(stored);
      if (notice?.sectionId !== this.sectionId) return;
      sessionStorage.removeItem(CartItemsComponent.#reloadNoticeKey);
    } catch {
      return;
    }

    if (typeof notice?.lineKey !== 'string' || !this.#cartLineForKey(notice.lineKey)) return;
    this.#showRemovalFailure(notice.lineKey, { delay: 500 });
  }

  /**
   * Cancels the optimistic removal animation for the in-flight request. Called
   * before a failed-removal recovery morph so the pending `onAnimationEnd`
   * callback cannot delete a row the recovery is about to restore.
   */
  #abortPendingRemovalAnimation() {
    this.#pendingEmptyCartSwap = null;
    for (const row of this.#pendingRemovalRows) {
      this.#abortedRemovalRows.add(row);
      this.#clearRemovalAnimationState(row);
    }
    this.#pendingRemovalRows = [];
  }

  /**
   * @param {HTMLElement} row
   */
  #clearRemovalAnimationState(row) {
    row.classList?.remove('removing');
    row.style?.removeProperty('--row-height');
  }

  /**
   * Restores the server-rendered cart after an optimistic removal fails.
   * @param {string} lineKey - The line-item key the failed request targeted.
   * @param {{ parsedError?: boolean }} [options] - `parsedError`: Shopify answered with
   *   an `.errors` body (dispatches INVALID, as upstream did); otherwise the request
   *   failed outright and the caller dispatches SERVICE_UNAVAILABLE.
   * @returns {Promise<boolean>} Whether the section was re-synced.
   */
  async #recoverFailedRemoval(lineKey, { parsedError = false } = {}) {
    this.#abortPendingRemovalAnimation();
    let lastError = null;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const sectionHTML = this.#validateSectionHTML(
          await this.#fetchFreshRecoverySectionHTML(),
          'Cart recovery response'
        );
        await morphSection(this.sectionId, sectionHTML, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        if (!this.#cartLineForKey(lineKey)) {
          throw new Error('Cart recovery response did not restore the failed line item');
        }

        this.#lineGeneration += 1;
        this.#updateCartQuantitySelectorButtonStates();
        if (!this.#restoreFailedRemovalFocus()) this.#restoreRemovalFocus();

        this.#showRemovalFailure(lineKey, { dispatch: parsedError });
        return true;
      } catch (error) {
        lastError = error;
        if (attempt === 0) {
          console.warn('[cart-items] Retrying failed-removal cart recovery with a fresh section request:', error);
          continue;
        }
      }
    }

    console.error('[cart-items] Failed to re-sync cart after removal failure:', lastError);
    this.#rememberReloadNotice(lineKey);
    window.location.reload();
    return false;
  }

  /**
   * Fetches a fresh section only for failed-removal recovery. This intentionally
   * bypasses the shared SectionRenderer cache path so Safari cannot reuse a stale
   * empty drawer response after an intercepted or failed cart removal.
   * @returns {Promise<string>}
   */
  async #fetchFreshRecoverySectionHTML() {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId.replace(/^shopify-section-/, ''));
    url.searchParams.set('_tcs_cart_recovery', `${Date.now()}-${++this.#cartRecoveryRequestSequence}`);
    url.searchParams.sort();

    const response = await fetch(url.toString(), { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Cart recovery section request failed with status ${response.status}`);
    }

    return response.text();
  }

  /**
   * TCS R-028e: shows the localized failed-removal copy on the restored row
   * identified by line-item key, visibly but not as a live region, and announces
   * it once in the polite status region. Shopify's `.errors` text is never shown
   * for a removal.
   * @param {string} lineKey
   * @param {{ dispatch?: boolean, delay?: number }} [options]
   */
  #showRemovalFailure(lineKey, { dispatch = false, delay } = {}) {
    const message = this.#cartFailureMessage();
    const row = this.#cartLineForKey(lineKey);
    const quantityInput = row?.querySelector('input[ref="quantityInput"]');

    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

    const cartItemErrorContainer = row?.querySelector('.cart-items__error');
    const cartItemError = cartItemErrorContainer?.querySelector('[ref^="cartItemError-"], small');

    if (!message) {
      console.warn('[cart-items] Failed-removal copy is missing: no [data-tcs-cart-status] region');
    } else if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      // Horizon wraps this text in role="alert". Drop the role for a removal
      // failure so the visible row copy is not live and the status region is the
      // only announcement; a section morph or #handleCartError puts it back.
      cartItemError.closest('[role="alert"]')?.removeAttribute('role');
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      console.warn('[cart-items] Failed removal error target was not found after cart re-sync');
    }

    this.#announceCartStatus(message, { delay });
    if (!dispatch) return;

    this.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code: 'INVALID',
      })
    );
  }

  /**
   * @param {string} lineKey
   * @returns {HTMLElement | null}
   */
  #cartLineForKey(lineKey) {
    if (!lineKey) return null;

    const root = this.#removalFocusRoot();
    return (
      [...root.querySelectorAll('.cart-items__table-row')].find(
        (row) => row instanceof HTMLElement && row.dataset.key === lineKey
      ) ?? null
    );
  }

  /**
   * @param {HTMLElement} removedRow
   * @param {EventTarget | null} [activatedControl] - The Remove button that was activated.
   */
  #captureFailedRemovalFocusSnapshot(removedRow, activatedControl) {
    let activeElement = document.activeElement;
    // TCS #394 follow-up: WebKit (Safari, VoiceOver double-tap and VO-Space) does not
    // focus an activated button, so focus is on the drawer dialog here. Use the
    // activated control in the removed row as the focus origin instead.
    if (
      activatedControl instanceof HTMLElement &&
      removedRow.contains(activatedControl) &&
      !(activeElement instanceof HTMLElement && removedRow.contains(activeElement))
    ) {
      activeElement = activatedControl;
    }
    if (!(activeElement instanceof HTMLElement)) {
      this.#failedRemovalFocusSnapshot = null;
      return;
    }

    const focusedRow = activeElement.closest('.cart-items__table-row');
    if (!(focusedRow instanceof HTMLElement) || focusedRow !== removedRow || !removedRow.dataset.key) {
      this.#failedRemovalFocusSnapshot = null;
      return;
    }

    const signature = this.#controlSignature(removedRow, activeElement);
    if (!signature) {
      this.#failedRemovalFocusSnapshot = null;
      return;
    }

    this.#failedRemovalFocusSnapshot = {
      key: removedRow.dataset.key,
      signature,
      automaticTarget: this.#automaticRemovalFocusSnapshot(),
    };
  }

  /**
   * @returns {null | { type: 'row', key: string, signature: { tag: string, ref: string | null, name: string | null, className: string, index: number } } | { type: 'empty' }}
   */
  #automaticRemovalFocusSnapshot() {
    const target = this.#pendingRemovalFocusTarget;
    if (!target) return null;
    if (target.type === 'empty') return { type: 'empty' };

    const focusTarget = this.#removalFocusTarget(target);
    const row = focusTarget?.closest('.cart-items__table-row');
    if (!(focusTarget instanceof HTMLElement) || !(row instanceof HTMLElement)) return null;

    const signature = this.#controlSignature(row, focusTarget);
    if (!signature) return null;

    return { type: 'row', key: target.key, signature };
  }

  /**
   * @param {Element} row
   * @param {HTMLElement} control
   * @returns {{ tag: string, ref: string | null, name: string | null, className: string, index: number } | null}
   */
  #controlSignature(row, control) {
    const controls = [...row.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => el instanceof HTMLElement);
    const index = controls.indexOf(control);
    if (index < 0) return null;

    return {
      tag: this.#elementTag(control),
      ref: this.#elementAttribute(control, 'ref'),
      name: this.#elementAttribute(control, 'name'),
      className: this.#elementClassName(control),
      index,
    };
  }

  /**
   * @returns {boolean} Whether focus was restored or intentionally left alone.
   */
  #restoreFailedRemovalFocus() {
    const snapshot = this.#failedRemovalFocusSnapshot;
    if (!snapshot) return false;

    if (
      this.#pendingRemovalHadUserInput &&
      this.#deliberateFocus &&
      this.#pendingRemovalFocusTarget?.type === 'row'
    ) {
      return this.#restoreDeliberateFocus(this.#pendingRemovalFocusTarget);
    }

    const activeElement = document.activeElement;
    const lostFocus =
      !(activeElement instanceof HTMLElement) ||
      !activeElement.isConnected ||
      activeElement === document.body ||
      activeElement === document.documentElement ||
      activeElement === this ||
      // TCS #394 follow-up: focus that fell back to the drawer dialog (or another
      // ancestor of the cart) is not a place the shopper chose.
      activeElement.contains(this);

    if (!lostFocus) {
      const activeRow = activeElement.closest('.cart-items__table-row');
      if (activeRow instanceof HTMLElement && activeRow.dataset.key === snapshot.key) return true;
      if (!this.#activeMatchesAutomaticRemovalTarget(activeElement, snapshot.automaticTarget)) return true;
    }

    const row = [...this.#removalFocusRoot().querySelectorAll('.cart-items__table-row')].find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.key === snapshot.key
    );
    if (!(row instanceof HTMLElement)) {
      console.warn('[cart-items] Failed removal row was not found after cart re-sync');
      return false;
    }

    const equivalent = this.#equivalentControl(row, snapshot.signature);
    if (!equivalent) {
      console.warn('[cart-items] Failed removal focus target was not found after cart re-sync');
      return false;
    }

    this.#isRestoringRemovalFocus = true;
    try {
      equivalent.focus({ preventScroll: true });
    } finally {
      this.#isRestoringRemovalFocus = false;
    }
    return document.activeElement === equivalent;
  }

  /**
   * @param {HTMLElement} activeElement
   * @param {null | { type: 'row', key: string, signature: { tag: string, ref: string | null, name: string | null, className: string, index: number } } | { type: 'empty' }} target
   * @returns {boolean}
   */
  #activeMatchesAutomaticRemovalTarget(activeElement, target) {
    if (!target) return false;
    if (target.type === 'empty') return activeElement.matches('.cart-items__empty-button');

    const activeRow = activeElement.closest('.cart-items__table-row');
    if (!(activeRow instanceof HTMLElement) || activeRow.dataset.key !== target.key) return false;

    return this.#equivalentControl(activeRow, target.signature) === activeElement;
  }

  #clearFailedRemovalFocusSnapshot() {
    this.#failedRemovalFocusSnapshot = null;
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * @param {HTMLTableRowElement} removedRow
   * @param {HTMLTableRowElement[]} rowsToRemove
   */
  #prepareRemovalFocusTarget(removedRow, rowsToRemove) {
    const rows = this.refs.cartItemRows ?? [];
    const removedKeys = rowsToRemove.map((row) => row.dataset.key).filter(Boolean);
    const removedKeySet = new Set(removedKeys);
    const removedIndex = rows.indexOf(removedRow);
    const survivingRows = rows.filter((row) => !removedKeySet.has(row.dataset.key));

    if (survivingRows.length === 0) {
      this.#emptyRemovalTrapRefreshTarget = null;
      this.#pendingRemovalFocusTarget = { type: 'empty', removedKeys };
      this.#pendingRemovalHadUserInput = false;
      this.#watchRemovalUserInput();
      return;
    }

    this.#emptyRemovalTrapRefreshTarget = null;
    const nextRow = rows.slice(removedIndex + 1).find((row) => !removedKeySet.has(row.dataset.key));
    const previousRow = rows
      .slice(0, Math.max(removedIndex, 0))
      .reverse()
      .find((row) => !removedKeySet.has(row.dataset.key));
    const targetKey = nextRow?.dataset.key ?? previousRow?.dataset.key ?? survivingRows[0]?.dataset.key;

    this.#pendingRemovalFocusTarget = targetKey ? { type: 'row', key: targetKey, removedKeys } : null;
    this.#pendingRemovalHadUserInput = false;
    this.#deliberateFocus = null;
    if (this.#pendingRemovalFocusTarget) this.#watchRemovalUserInput();
  }

  #queueRemovalFocusRestore() {
    const target = this.#pendingRemovalFocusTarget;
    if (!target) return;

    requestAnimationFrame(() => {
      if (this.#pendingRemovalFocusTarget === target) {
        this.#restoreRemovalFocus();
      }
    });
  }

  #completeRemovalFocusRestore() {
    const target = this.#pendingRemovalFocusTarget;
    if (!target) return;

    if (target.type === 'empty') {
      this.#restoreRemovalFocus();
      if (this.#pendingRemovalFocusTarget === target) {
        this.#verifyEmptyRemovalFocusRestore(target);
      }
      return;
    }

    if (this.#deliberateFocus) {
      this.#restoreRemovalFocus();
      if (this.#pendingRemovalFocusTarget === target) this.#verifyDeliberateFocus(target);
      return;
    }

    if (this.#restoreRemovalFocus()) {
      this.#clearRemovalFocusRestore();
      return;
    }

    this.#queueRemovalFocusRestore();
  }

  /**
   * TCS A11Y-LA-07 (R-028d): the visitor deliberately had focus in the continued
   * row. Never move it while it is still on a live control there (the response
   * and the morph can land in any order, so this stays pending rather than
   * clearing); if the morph replaced that node and focus fell away, put it on the
   * equivalent control (same row key, same control) in the new DOM.
   * @param {{ type: 'row', key: string, removedKeys: string[] }} target
   * @returns {boolean}
   */
  #restoreDeliberateFocus(target) {
    const deliberate = this.#deliberateFocus;
    if (!deliberate) return true;

    const active = document.activeElement;
    const lost =
      !(active instanceof HTMLElement) ||
      !active.isConnected ||
      active === document.body ||
      active === document.documentElement ||
      active === this;
    const activeRow = lost ? null : active.closest('.cart-items__table-row');
    const activeKey = activeRow instanceof HTMLElement ? (activeRow.dataset.key ?? '') : '';

    if (!lost && activeKey === target.key) return false; // still on a live control in the row: keep waiting
    if (!lost && !target.removedKeys.includes(activeKey)) {
      // Focus is somewhere else the visitor put it: not ours to move.
      this.#clearRemovalFocusRestore();
      return true;
    }

    const row = [...this.#removalFocusRoot().querySelectorAll('.cart-items__table-row')].find(
      (candidate) => candidate instanceof HTMLElement && candidate.dataset.key === deliberate.key
    );
    if (!row) return false;
    const equivalent = this.#equivalentControl(row, deliberate.signature);
    if (!equivalent) return false;

    this.#isRestoringRemovalFocus = true;
    try {
      equivalent.focus({ preventScroll: true });
    } finally {
      this.#isRestoringRemovalFocus = false;
    }
    if (document.activeElement !== equivalent) return false;
    this.#recordDeliberateFocus(equivalent);
    return true;
  }

  /**
   * @param {Element} row
   * @param {{ tag: string, ref: string | null, name: string | null, className: string, index: number }} signature
   * @returns {HTMLElement | null}
   */
  #equivalentControl(row, signature) {
    const controls = [...row.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => el instanceof HTMLElement);
    const same = controls.filter(
      (el) =>
        this.#elementTag(el) === signature.tag &&
        this.#elementAttribute(el, 'ref') === signature.ref &&
        this.#elementAttribute(el, 'name') === signature.name &&
        this.#elementClassName(el) === signature.className
    );
    return same.find((el) => controls.indexOf(el) === signature.index) ?? same[0] ?? controls[signature.index] ?? null;
  }

  /**
   * @param {Element} element
   * @returns {string}
   */
  #elementTag(element) {
    return element.localName || element.tagName?.toLowerCase() || '';
  }

  /**
   * @param {Element} element
   * @param {string} name
   * @returns {string | null}
   */
  #elementAttribute(element, name) {
    if (typeof element.getAttribute === 'function') return element.getAttribute(name);
    const value = /** @type {{ attributes?: Record<string, unknown> }} */ (element).attributes?.[name];
    return typeof value === 'string' ? value : null;
  }

  /**
   * @param {Element} element
   * @returns {string}
   */
  #elementClassName(element) {
    if (typeof element.className === 'string') return element.className;
    return this.#elementAttribute(element, 'class') ?? '';
  }

  /**
   * Keeps the deliberate-focus restore pending long enough to repair a morph
   * that lands after the response was handled (the morph is not observable from
   * `morphSection`'s return), while real focus moves still win.
   * @param {{ type: 'row', key: string, removedKeys: string[] }} target
   */
  #verifyDeliberateFocus(target) {
    this.#clearRemovalFocusVerificationTimers();
    if (typeof globalThis.setTimeout !== 'function') return;

    for (const delay of [16, 50, 120, 250, 500, 900, 1500]) {
      const timer = globalThis.setTimeout(() => {
        if (this.#pendingRemovalFocusTarget !== target) return;
        this.#restoreRemovalFocus();
        if (this.#pendingRemovalFocusTarget === target && delay === 1500) this.#clearRemovalFocusRestore();
      }, delay);
      this.#removalFocusVerificationTimers.push(timer);
    }
  }

  /**
   * @param {Element | null} element
   */
  #recordDeliberateFocus(element) {
    const target = this.#pendingRemovalFocusTarget;
    if (!target || target.type !== 'row' || !(element instanceof HTMLElement)) return;

    const row = element.closest('.cart-items__table-row');
    if (!(row instanceof HTMLElement) || row.dataset.key !== target.key) return;

    const controls = [...row.querySelectorAll(FOCUSABLE_SELECTOR)];
    this.#deliberateFocus = {
      node: element,
      key: target.key,
      signature: {
        tag: this.#elementTag(element),
        ref: this.#elementAttribute(element, 'ref'),
        name: this.#elementAttribute(element, 'name'),
        className: this.#elementClassName(element),
        index: controls.indexOf(element),
      },
    };
  }

  /**
   * @param {FocusEvent} event
   */
  #onRemovalFocusIn = (event) => {
    if (this.#isRestoringRemovalFocus) return;
    const target = this.#pendingRemovalFocusTarget;
    if (!target || target.type !== 'row' || !(event.target instanceof HTMLElement)) return;

    const row = event.target.closest('.cart-items__table-row');
    const key = row instanceof HTMLElement ? (row.dataset.key ?? '') : '';
    if (key === target.key) {
      this.#recordDeliberateFocus(event.target);
    } else if (this.#deliberateFocus && !target.removedKeys.includes(key)) {
      this.#deliberateFocus = null; // the visitor moved on; ordinary intent rules apply
    }
  };

  /**
   * @returns {boolean} Whether focus restoration is complete.
   */
  #restoreRemovalFocus() {
    const target = this.#pendingRemovalFocusTarget;
    if (!target) return true;

    if (target.type === 'row' && this.#deliberateFocus) return this.#restoreDeliberateFocus(target);

    const focusTarget = this.#removalFocusTarget(target);

    if (this.#hasIntentionalFocus(target, focusTarget)) {
      this.#clearRemovalFocusRestore();
      return true;
    }

    if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) {
      if (target.type === 'empty') this.#refreshEmptyDrawerFocusTrap(focusTarget);
      focusTarget.focus({ preventScroll: true });
      return document.activeElement === focusTarget;
    }

    return false;
  }

  /**
   * Some browser/AT combinations can ignore early focus on the newly inserted
   * empty-state link, or briefly accept it and then drop DOM focus to <body>.
   * Keep the empty-state restore pending long enough to repair BODY, but let
   * real focus moves win.
   * @param {{ type: 'empty', removedKeys: string[] }} target
   */
  #verifyEmptyRemovalFocusRestore(target) {
    this.#clearRemovalFocusVerificationTimers();
    if (typeof globalThis.setTimeout !== 'function') return;

    for (const delay of [50, 250, 500, 850, 1250, 1500, 1750, 2500]) {
      const timer = globalThis.setTimeout(() => {
        if (this.#pendingRemovalFocusTarget !== target) return;

        const restored = this.#restoreRemovalFocus();
        if (this.#pendingRemovalFocusTarget !== target) return;

        if (delay === 2500) {
          this.#clearRemovalFocusRestore();
        }
      }, delay);
      this.#removalFocusVerificationTimers.push(timer);
    }
  }

  #clearRemovalFocusVerificationTimers() {
    if (typeof globalThis.clearTimeout === 'function') {
      for (const timer of this.#removalFocusVerificationTimers) globalThis.clearTimeout(timer);
    }
    this.#removalFocusVerificationTimers = [];
  }

  #clearRemovalFocusRestore() {
    this.#pendingRemovalFocusTarget = null;
    this.#emptyRemovalTrapRefreshTarget = null;
    this.#pendingRemovalHadUserInput = false;
    this.#deliberateFocus = null;
    this.#clearRemovalFocusVerificationTimers();
    this.#unwatchRemovalUserInput();
  }

  /**
   * @param {{ type: 'row', key: string, removedKeys: string[] } | { type: 'empty', removedKeys: string[] }} target
   */
  #removalFocusTarget(target) {
    const focusRoot = this.#removalFocusRoot();
    if (target.type === 'row') {
      const targetRow = [...focusRoot.querySelectorAll('.cart-items__table-row')].find(
        (row) => row.dataset.key === target.key
      );
      return (
        targetRow?.querySelector('.cart-items__remove:not([hidden])') ??
        targetRow?.querySelector('input:not([disabled])') ??
        targetRow?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') ??
        null
      );
    }

    // A selector list returns the first DOM match, not the first selector's
    // match. The empty state can contain a CSS-hidden account link before the
    // shopping CTA. Wait for the intended CTA instead of targeting that link.
    return focusRoot.querySelector('.cart-items__empty-button');
  }

  #removalFocusRoot() {
    if (document.contains(this)) return this;

    const liveComponent = [...document.querySelectorAll('cart-items-component')].find(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === this.dataset.sectionId
    );

    return liveComponent instanceof HTMLElement ? liveComponent : this;
  }

  /**
   * Desktop cart drawers use the theme focus trap, which snapshots focusable
   * elements when the drawer opens. The empty CTA is inserted later, so refresh
   * the trap once for each inserted empty CTA. `trapFocus()` focuses the drawer
   * panel; repeating it before every retry can keep focus on the panel instead
   * of giving the CTA a clean follow-up focus attempt.
   * @param {HTMLElement} focusTarget
   */
  #refreshEmptyDrawerFocusTrap(focusTarget) {
    if (!this.isDrawer) return;
    if (this.#emptyRemovalTrapRefreshTarget === focusTarget) return;

    const panel = focusTarget.closest('dialog[open]');
    if (!(panel instanceof HTMLElement)) return;
    if (panel.matches(':modal')) return;
    if (panel.querySelector('dialog[open]')) return;

    trapFocus(panel);
    this.#emptyRemovalTrapRefreshTarget = focusTarget;
  }

  /**
   * @param {{ type?: string, removedKeys: string[] }} target
   * @param {Element | null} focusTarget
   */
  #hasIntentionalFocus(target, focusTarget) {
    const activeElement = document.activeElement;

    if (!(activeElement instanceof HTMLElement)) return false;
    if (!activeElement.isConnected) return false;
    if (activeElement === document.body) return false;
    if (activeElement === document.documentElement) return false;
    if (activeElement === this) return false;

    // Safari/WebKit can leave focus on the drawer dialog when a pointer or
    // assistive-technology activation clicks Remove without first focusing the
    // button. The dialog is an ancestor fallback, not a destination the shopper
    // chose. Treat it like BODY so R-028 can continue focus to the surviving row.
    const focusRoot = this.#removalFocusRoot();
    if (focusRoot instanceof HTMLElement && activeElement.contains(focusRoot)) return false;

    const activeRow = activeElement.closest('.cart-items__table-row');
    if (activeRow instanceof HTMLElement && target.removedKeys.includes(activeRow.dataset.key ?? '')) return false;
    if (target.type === 'row' && activeRow instanceof HTMLElement && activeRow.dataset.key === target.key) {
      return this.#pendingRemovalHadUserInput;
    }

    if (target.type === 'empty') {
      if (focusTarget instanceof HTMLElement && activeElement.contains(focusTarget)) return false;
      if (!this.#pendingRemovalHadUserInput) return false;
      if (focusRoot instanceof HTMLElement && focusRoot.contains(activeElement) && !this.#isActionableFocus(activeElement)) {
        return false;
      }
    }

    return true;
  }

  /**
   * @param {HTMLElement} element
   */
  #isActionableFocus(element) {
    return element.matches(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  /**
   * @param {Event} event
   */
  #markRemovalUserInput = (event) => {
    this.#pendingRemovalHadUserInput = true;

    // A press on some other element is the visitor leaving the row (including a
    // deliberate blur onto empty space): stop treating the row as theirs.
    const pressed = event.target;
    const target = this.#pendingRemovalFocusTarget;
    if (
      (event.type === 'pointerdown' || event.type === 'touchstart') &&
      pressed instanceof Element &&
      target?.type === 'row' &&
      pressed.closest('.cart-items__table-row')?.getAttribute('data-key') !== target.key
    ) {
      this.#deliberateFocus = null;
      return;
    }
    this.#recordDeliberateFocus(document.activeElement);
  };

  #watchRemovalUserInput() {
    this.#unwatchRemovalUserInput();
    if (typeof document.addEventListener !== 'function') return;
    document.addEventListener('keydown', this.#markRemovalUserInput, true);
    document.addEventListener('pointerdown', this.#markRemovalUserInput, true);
    document.addEventListener('touchstart', this.#markRemovalUserInput, true);
    document.addEventListener('focusin', this.#onRemovalFocusIn, true);
  }

  #unwatchRemovalUserInput() {
    if (typeof document.removeEventListener !== 'function') return;
    document.removeEventListener('keydown', this.#markRemovalUserInput, true);
    document.removeEventListener('pointerdown', this.#markRemovalUserInput, true);
    document.removeEventListener('touchstart', this.#markRemovalUserInput, true);
    document.removeEventListener('focusin', this.#onRemovalFocusIn, true);
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
